import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  REVIEW_STATUS,
  REVIEW_VOTE,
  applyVoteTransition,
  buildReviewId,
  canMutateByDailyLimit,
  computeReviewAggregates,
  createApiError,
  getUtcDayKey,
  hasVerifiedBorrowRecord,
  toMillis,
  validateReviewPayload,
} from './reviewUtils';

const REVIEWS_COL = 'reviews';
const REVIEW_VOTES_COL = 'reviewVotes';
const REVIEW_REPORTS_COL = 'reviewReports';
const REVIEW_STATS_COL = 'bookReviewStats';
const REVIEW_RATE_COL = 'reviewRateLimits';
const BORROWS_COL = 'borrows';
const BOOK_BORROWS_COL = 'bookBorrows';

function mapReviewDoc(snap) {
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    createdAtMs: toMillis(data?.createdAt),
    updatedAtMs: toMillis(data?.updatedAt),
  };
}

async function getUserCreatedAt(user) {
  if (!user?.uid) return null;
  try {
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    if (userSnap.exists()) {
      const createdAt = userSnap.data()?.createdAt;
      const createdMs = toMillis(createdAt);
      if (createdMs) return createdMs;
    }
  } catch {
    // noop
  }
  const authCreated = toMillis(user?.metadata?.creationTime);
  return authCreated || null;
}

async function queryBorrowRows(colName, wherePairs = []) {
  try {
    let q = query(collection(db, colName));
    for (const pair of wherePairs) {
      q = query(q, where(pair.field, '==', pair.value));
    }
    q = query(q, limit(10));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  } catch {
    return [];
  }
}

function parseReviewBookScope(bookId) {
  const raw = String(bookId || '');
  if (raw.startsWith('isbn:')) {
    return { isbn: raw.slice(5), templateId: null };
  }
  return { isbn: null, templateId: raw || null };
}

function toActionDateLabel(ms) {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('uk-UA');
}

async function getLatestUserBookActionHint({ userId, bookId }) {
  if (!userId || !bookId) return '';
  const { isbn, templateId } = parseReviewBookScope(bookId);

  const borrowsCandidates = [];
  if (templateId) {
    borrowsCandidates.push(
      queryBorrowRows(BORROWS_COL, [
        { field: 'userUid', value: userId },
        { field: 'templateId', value: templateId },
      ]),
    );
  }
  if (isbn) {
    borrowsCandidates.push(
      queryBorrowRows(BORROWS_COL, [
        { field: 'userUid', value: userId },
        { field: 'isbn', value: isbn },
      ]),
    );
  }

  if (!borrowsCandidates.length) return '';

  const resolved = (await Promise.all(borrowsCandidates)).flat();
  const filtered = resolved
    .map((row) => ({
      action: String(row?.action || ''),
      createdAtMs: toMillis(row?.createdAt),
    }))
    .filter((row) => (row.action === 'take' || row.action === 'add') && row.createdAtMs);

  if (!filtered.length) return '';
  filtered.sort((a, b) => b.createdAtMs - a.createdAtMs);
  const latest = filtered[0];
  const dateLabel = toActionDateLabel(latest.createdAtMs);

  if (latest.action === 'take') return `Взяв цю книгу ${dateLabel}`;
  if (latest.action === 'add') return `Додав цю книгу в книгообіг ${dateLabel}`;
  return '';
}

export async function isVerifiedReaderForBook({ userId, bookId, isbn, templateId }) {
  if (!userId) return false;

  const candidates = [];

  if (bookId) {
    candidates.push(
      queryBorrowRows(BORROWS_COL, [
        { field: 'userUid', value: userId },
        { field: 'bookId', value: bookId },
      ]),
      queryBorrowRows(BOOK_BORROWS_COL, [
        { field: 'user_id', value: userId },
        { field: 'book_id', value: bookId },
      ]),
    );
  }
  if (isbn) {
    candidates.push(
      queryBorrowRows(BORROWS_COL, [
        { field: 'userUid', value: userId },
        { field: 'isbn', value: isbn },
      ]),
      queryBorrowRows(BOOK_BORROWS_COL, [
        { field: 'user_id', value: userId },
        { field: 'isbn', value: isbn },
      ]),
    );
  }
  if (templateId) {
    candidates.push(
      queryBorrowRows(BORROWS_COL, [
        { field: 'userUid', value: userId },
        { field: 'templateId', value: templateId },
      ]),
      queryBorrowRows(BOOK_BORROWS_COL, [
        { field: 'user_id', value: userId },
        { field: 'template_id', value: templateId },
      ]),
    );
  }

  const resolved = await Promise.all(candidates);
  return resolved.some((rows) => hasVerifiedBorrowRecord(rows));
}

export async function recalculateBookReviewStats(bookId) {
  if (!bookId) return null;

  const snap = await getDocs(
    query(
      collection(db, REVIEWS_COL),
      where('bookId', '==', bookId),
      where('status', '==', REVIEW_STATUS.ACTIVE),
      limit(1000),
    ),
  );
  const rows = snap.docs.map((d) => d.data());
  const aggregates = computeReviewAggregates(rows);

  const statsRef = doc(db, REVIEW_STATS_COL, String(bookId));
  await setDoc(
    statsRef,
    {
      bookId,
      ...aggregates,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return aggregates;
}

export async function getBookReviewStats(bookId) {
  if (!bookId) return null;
  const ref = doc(db, REVIEW_STATS_COL, String(bookId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data();
}

export async function getBookRatingDistribution(bookId) {
  const empty = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  if (!bookId) return empty;

  const snap = await getDocs(
    query(
      collection(db, REVIEWS_COL),
      where('bookId', '==', bookId),
      where('status', '==', REVIEW_STATUS.ACTIVE),
      limit(1000),
    ),
  );

  const dist = { ...empty };
  snap.docs.forEach((d) => {
    const r = Number(d.data()?.rating || 0);
    if (r >= 1 && r <= 5) dist[r] += 1;
  });
  return dist;
}

export async function getOwnReview({ bookId, userId }) {
  if (!bookId || !userId) return null;
  const reviewId = buildReviewId(bookId, userId);
  const snap = await getDoc(doc(db, REVIEWS_COL, reviewId));
  return snap.exists() ? mapReviewDoc(snap) : null;
}

async function getUserVoteForReview(reviewId, userId) {
  if (!reviewId || !userId) return null;
  const voteId = buildReviewId(reviewId, userId);
  const snap = await getDoc(doc(db, REVIEW_VOTES_COL, voteId));
  return snap.exists() ? snap.data()?.voteType || null : null;
}

export async function listBookReviews({
  bookId,
  currentUserId = null,
  verifiedOnly = false,
  sort = 'newest',
}) {
  if (!bookId) return [];

  let q = query(
    collection(db, REVIEWS_COL),
    where('bookId', '==', bookId),
    where('status', '==', REVIEW_STATUS.ACTIVE),
  );

  if (sort === 'helpful') {
    q = query(q, orderBy('helpfulCount', 'desc'), orderBy('createdAt', 'desc'), limit(100));
  } else {
    q = query(q, orderBy('createdAt', 'desc'), limit(100));
  }

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => mapReviewDoc(d));

  const withAction = await Promise.all(
    rows.map(async (row) => {
      const lastBookActionHint = await getLatestUserBookActionHint({
        userId: row.userId,
        bookId: row.bookId,
      });
      const scope = parseReviewBookScope(row.bookId);
      const dynamicVerified = await isVerifiedReaderForBook({
        userId: row.userId,
        bookId: null,
        isbn: scope.isbn,
        templateId: scope.templateId,
      });

      return {
        ...row,
        lastBookActionHint,
        isVerifiedReader: Boolean(row.isVerifiedReader || dynamicVerified),
      };
    }),
  );

  const filteredRows = verifiedOnly
    ? withAction.filter((row) => Boolean(row.isVerifiedReader))
    : withAction;

  if (!currentUserId) return filteredRows;

  const withVotes = await Promise.all(
    filteredRows.map(async (row) => ({
      ...row,
      userVote: await getUserVoteForReview(row.id, currentUserId),
    })),
  );
  return withVotes;
}

export async function upsertReview({
  user,
  bookId,
  isbn = null,
  templateId = null,
  rating,
  reviewText = '',
}) {
  if (!user?.uid) throw createApiError(401, 'Потрібна авторизація.');
  if (!bookId) throw createApiError(400, 'Відсутній ідентифікатор книги.');

  const payload = validateReviewPayload({ rating, reviewText });
  const reviewId = buildReviewId(bookId, user.uid);
  const reviewRef = doc(db, REVIEWS_COL, reviewId);
  const dayKey = getUtcDayKey();
  const rateRef = doc(db, REVIEW_RATE_COL, `${user.uid}__${dayKey}`);

  const userCreatedAtMs = await getUserCreatedAt(user);
  const verifiedReader = await isVerifiedReaderForBook({
    userId: user.uid,
    bookId,
    isbn,
    templateId,
  });

  await runTransaction(db, async (tx) => {
    const reviewSnap = await tx.get(reviewRef);
    const rateSnap = await tx.get(rateRef);

    const rateData = rateSnap.exists() ? rateSnap.data() : null;
    const dailyCount = Number(rateData?.count || 0);
    const limitCheck = canMutateByDailyLimit({ createdAt: userCreatedAtMs, dailyCount });
    if (!limitCheck.allowed) {
      throw createApiError(429, 'Для нових акаунтів дозволено до 3 змін відгуків на добу.');
    }

    const prev = reviewSnap.exists() ? reviewSnap.data() : null;
    const nextDailyCount = dailyCount + 1;

    // Important for Firestore rules: owner update mutates only editable fields.
    const reviewPayload = prev
      ? {
          rating: payload.rating,
          reviewText: payload.reviewText,
          isVerifiedReader: verifiedReader,
          userDisplayName: user.displayName || user.email || 'Користувач',
          userPhotoURL: user.photoURL || null,
          updatedAt: serverTimestamp(),
        }
      : {
          bookId,
          userId: user.uid,
          userDisplayName: user.displayName || user.email || 'Користувач',
          userPhotoURL: user.photoURL || null,
          rating: payload.rating,
          reviewText: payload.reviewText,
          isVerifiedReader: verifiedReader,
          status: REVIEW_STATUS.ACTIVE,
          helpfulCount: 0,
          unhelpfulCount: 0,
          reportCount: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

    tx.set(reviewRef, reviewPayload, { merge: true });

    tx.set(
      rateRef,
      {
        userId: user.uid,
        dayKey,
        count: nextDailyCount,
        createdAt: rateData?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  await recalculateBookReviewStats(bookId);

  const saved = await getDoc(reviewRef);
  return saved.exists() ? mapReviewDoc(saved) : null;
}

export async function deleteOwnReview({ user, bookId }) {
  if (!user?.uid) throw createApiError(401, 'Потрібна авторизація.');
  if (!bookId) throw createApiError(400, 'Відсутній ідентифікатор книги.');

  const reviewId = buildReviewId(bookId, user.uid);
  const reviewRef = doc(db, REVIEWS_COL, reviewId);

  await runTransaction(db, async (tx) => {
    const reviewSnap = await tx.get(reviewRef);
    if (!reviewSnap.exists()) return;
    const ownerId = reviewSnap.data()?.userId;
    if (ownerId !== user.uid) {
      throw createApiError(403, 'Можна видаляти лише свій відгук.');
    }
    tx.delete(reviewRef);
  });

  await recalculateBookReviewStats(bookId);
}

export async function reportReview({ reviewId, user, reason = '' }) {
  if (!user?.uid) throw createApiError(401, 'Потрібна авторизація.');
  if (!reviewId) throw createApiError(400, 'Відсутній reviewId.');

  const trimmedReason = String(reason || '').trim();
  if (trimmedReason.length > 500) {
    throw createApiError(400, 'Причина скарги не може бути довшою за 500 символів.');
  }

  const reviewRef = doc(db, REVIEWS_COL, reviewId);

  await runTransaction(db, async (tx) => {
    const reviewSnap = await tx.get(reviewRef);
    if (!reviewSnap.exists()) throw createApiError(404, 'Відгук не знайдено.');

    tx.set(
      reviewRef,
      {
        reportCount: Number(reviewSnap.data()?.reportCount || 0) + 1,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  await addDoc(collection(db, REVIEW_REPORTS_COL), {
    reviewId,
    reporterUserId: user.uid,
    reason: trimmedReason || null,
    status: 'open',
    createdAt: serverTimestamp(),
  });
}

export async function setReviewHidden({ reviewId, hidden, actorUid }) {
  if (!actorUid) throw createApiError(401, 'Потрібна авторизація.');
  if (!reviewId) throw createApiError(400, 'Відсутній reviewId.');

  const actorSnap = await getDoc(doc(db, 'users', actorUid));
  const role = actorSnap.exists() ? actorSnap.data()?.role : null;
  if (role !== 'admin') throw createApiError(403, 'Лише адміністратор може змінювати статус review.');

  const reviewRef = doc(db, REVIEWS_COL, reviewId);
  const reviewSnap = await getDoc(reviewRef);
  if (!reviewSnap.exists()) throw createApiError(404, 'Відгук не знайдено.');

  const nextStatus = hidden ? REVIEW_STATUS.HIDDEN : REVIEW_STATUS.ACTIVE;
  await setDoc(
    reviewRef,
    {
      status: nextStatus,
      hiddenBy: hidden ? actorUid : null,
      hiddenAt: hidden ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  const bookId = reviewSnap.data()?.bookId;
  if (bookId) {
    await recalculateBookReviewStats(bookId);
  }
}

export async function voteReview({ reviewId, userId, voteType }) {
  if (!userId) throw createApiError(401, 'Потрібна авторизація.');
  if (!reviewId) throw createApiError(400, 'Відсутній reviewId.');
  if (
    voteType !== REVIEW_VOTE.HELPFUL &&
    voteType !== REVIEW_VOTE.UNHELPFUL &&
    voteType !== null
  ) {
    throw createApiError(400, 'Невірний тип голосу.');
  }

  const reviewRef = doc(db, REVIEWS_COL, reviewId);
  const voteId = buildReviewId(reviewId, userId);
  const voteRef = doc(db, REVIEW_VOTES_COL, voteId);

  return runTransaction(db, async (tx) => {
    const reviewSnap = await tx.get(reviewRef);
    if (!reviewSnap.exists()) throw createApiError(404, 'Відгук не знайдено.');
    if (reviewSnap.data()?.status !== REVIEW_STATUS.ACTIVE) {
      throw createApiError(400, 'Голосування доступне лише для активних відгуків.');
    }

    const voteSnap = await tx.get(voteRef);
    const currentVote = voteSnap.exists() ? voteSnap.data()?.voteType || null : null;
    const nextVote = currentVote === voteType ? null : voteType;

    const nextCounts = applyVoteTransition({
      currentVote,
      nextVote,
      helpfulCount: reviewSnap.data()?.helpfulCount || 0,
      unhelpfulCount: reviewSnap.data()?.unhelpfulCount || 0,
    });

    tx.set(
      reviewRef,
      {
        helpfulCount: nextCounts.helpfulCount,
        unhelpfulCount: nextCounts.unhelpfulCount,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    if (nextVote) {
      const prevCreated = voteSnap.exists() ? voteSnap.data()?.createdAt : null;
      tx.set(
        voteRef,
        {
          reviewId,
          userId,
          voteType: nextVote,
          createdAt: prevCreated || serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } else {
      tx.delete(voteRef);
    }

    return { nextVote, ...nextCounts };
  });
}
